import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../../components/PageHeader';
import { apiFetch } from '../../../utils/api';
import { formatCurrency } from '../../../utils/currency';

function Proformas() {
  const navigate = useNavigate();
  const [proformas, setProformas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchProformas();
  }, []);

  const fetchProformas = async () => {
    setLoading(true);
    setLoadError('');
    try {
      const response = await apiFetch('/api/proformas');
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Proforma list failed with status ${response.status}`);
      }
      const data = await response.json();
      setProformas(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching proformas:', error);
      setLoadError(error.response?.data?.error || error.message || 'Could not load proformas');
      setProformas([]);
    } finally {
      setLoading(false);
    }
  };

  const filtered = proformas.filter((proforma) => {
    const term = searchTerm.toLowerCase();
    return (
      proforma.proforma_ref?.toLowerCase().includes(term) ||
      proforma.customer_name?.toLowerCase().includes(term) ||
      proforma.status?.toLowerCase().includes(term)
    );
  });

  const statusClass = (status) => {
    const classes = {
      draft: 'bg-gray-100 text-gray-700',
      sent: 'bg-blue-100 text-blue-700',
      accepted: 'bg-green-100 text-green-700',
      converted: 'bg-purple-100 text-purple-700',
      invoiced: 'bg-emerald-100 text-emerald-700',
      cancelled: 'bg-red-100 text-red-700'
    };
    return classes[status] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Proformas"
        subtitle={`${proformas.length} total`}
        backButton
        onBack={() => navigate('/finance')}
        backTitle="Back to Financials"
        sticky
        actionButton={
          <button
            onClick={() => navigate('/finance/proformas/new')}
            className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg font-medium transition text-sm"
          >
            New Proforma
          </button>
        }
      />

      <main className="px-4 py-3">
        <div className="mb-3 flex items-center gap-2">
          <input
            type="text"
            placeholder="Search proformas..."
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            className="flex-1 max-w-md px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
          />
          <button
            onClick={fetchProformas}
            className="px-3 py-1.5 bg-white border border-gray-300 rounded text-sm font-medium hover:bg-gray-50 transition"
          >
            Refresh
          </button>
        </div>

        <div className="bg-white rounded border border-gray-200">
          <div className="overflow-auto max-h-[calc(100vh-240px)]">
            <table className="w-full text-sm">
              <thead className="bg-gray-300 sticky top-0 z-20 border-b border-gray-400">
                <tr>
                  <th className="text-left px-3 py-1.5 text-xs font-semibold text-gray-600">Proforma Ref</th>
                  <th className="text-left px-3 py-1.5 text-xs font-semibold text-gray-600">Customer</th>
                  <th className="text-left px-3 py-1.5 text-xs font-semibold text-gray-600">Site</th>
                  <th className="text-left px-3 py-1.5 text-xs font-semibold text-gray-600">Date</th>
                  <th className="text-right px-3 py-1.5 text-xs font-semibold text-gray-600">Total</th>
                  <th className="text-center px-3 py-1.5 text-xs font-semibold text-gray-600">Status</th>
                  <th className="text-right px-3 py-1.5 text-xs font-semibold text-gray-600">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr>
                    <td colSpan="7" className="px-3 py-6 text-center text-gray-500">
                      Loading proformas...
                    </td>
                  </tr>
                ) : loadError ? (
                  <tr>
                    <td colSpan="7" className="px-3 py-6">
                      <div className="max-w-xl mx-auto bg-red-50 border border-red-200 rounded-lg p-4">
                        <div className="font-semibold text-red-900 mb-1">Proformas could not load</div>
                        <div className="text-red-800 mb-3">{loadError}</div>
                        <button
                          onClick={fetchProformas}
                          className="px-3 py-1.5 bg-red-600 text-white rounded text-xs font-medium hover:bg-red-700 transition"
                        >
                          Retry
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-3 py-6 text-center text-gray-500">
                      {searchTerm ? 'No matches.' : 'No proformas yet.'}
                    </td>
                  </tr>
                ) : (
                  filtered.map((proforma, index) => (
                    <tr
                      key={proforma.id}
                      onClick={() => navigate(`/finance/proformas/${proforma.id}`)}
                      className={`hover:bg-blue-200 cursor-pointer transition ${index % 2 === 1 ? 'bg-blue-100' : 'bg-white'}`}
                    >
                      <td className="px-3 py-2 font-medium text-gray-900">{proforma.proforma_ref}</td>
                      <td className="px-3 py-2 text-gray-700">{proforma.customer_name}</td>
                      <td className="px-3 py-2 text-gray-700">{proforma.site_name || 'Head Office'}</td>
                      <td className="px-3 py-2 text-gray-700">
                        {proforma.proforma_date ? new Date(proforma.proforma_date).toLocaleDateString() : '-'}
                      </td>
                      <td className="px-3 py-2 text-right font-medium">{formatCurrency(proforma.total)}</td>
                      <td className="px-3 py-2 text-center">
                        <span className={`inline-flex px-2 py-0.5 text-xs rounded ${statusClass(proforma.status)}`}>
                          {proforma.status || 'draft'}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button
                          onClick={(event) => {
                            event.stopPropagation();
                            navigate(`/finance/proformas/${proforma.id}`);
                          }}
                          className="text-purple-600 hover:text-purple-800 text-xs font-medium"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}

export default Proformas;
